import type { CSSProperties } from 'react';
import './MicroBarChart.css';

export interface MicroBar {
  /** The raw value. Scaled against `ceiling` (or the series max) for height. */
  value: number;
  /** What this column is, e.g. "week 7". Used in the accessible summary. */
  label: string;
}

export interface MicroBarChartProps {
  bars: MicroBar[];
  /**
   * What the series measures, e.g. "Duplicate rate, 12 weeks". **Required** — a bare
   * row of divs is invisible to assistive tech, and this component's entire content
   * is proportion.
   */
  label: string;
  /**
   * A fixed ceiling to scale against. The prototype uses one where the scale is
   * meaningful (a percentage, a target) and the series max where it is not — and the
   * distinction matters: scaling to the max makes the tallest bar full height
   * *always*, which reads as "at maximum" when it may be 0.4%.
   */
  ceiling?: number;
  /** Formats a value for the summary and the per-bar tooltip. */
  format?: (value: number) => string;
  tone?: 'accent' | 'success' | 'warning' | 'danger';
  size?: 'sm' | 'md';
  /** Axis captions — the prototype writes these literally, with no tick marks. */
  captions?: [string, string];
}

/**
 * A column sparkline (design-system catalogue, Tier 3).
 *
 * **3 of the prototype's 18 page files** — finding trend over 12 weeks, AI QA
 * disagreement over 8 weeks, dashboard quality (prototype/CENSUS.md §2a item 18).
 *
 * **The current column takes the strong tone and every prior column its pale
 * companion.** That is the whole visual idea: the eye lands on now, and the history is
 * context. Losing it makes the series a wall of one colour.
 *
 * Deliberately minimal, matching the design: **no axis, no gridlines, no y-scale.**
 * The prototype's only per-bar affordance is a native `title`, which is unreachable by
 * keyboard and invisible on touch — so this also renders a **text summary** of the
 * whole series, which is the accessible equivalent and cheap.
 *
 * There is no donut, pie, line or area chart anywhere in the prototype's 10,038 lines.
 * This plus `MetricBarRow` and `ProgressBar` is the entire charting surface; anything
 * else would be inventing a visual language.
 */
export function MicroBarChart({
  bars,
  label,
  ceiling,
  format = (v) => String(v),
  tone = 'accent',
  size = 'md',
  captions,
}: MicroBarChartProps) {
  const max = ceiling ?? Math.max(...bars.map((b) => b.value), 1);
  const summary = `${label}. ${bars.map((b) => `${b.label}: ${format(b.value)}`).join(', ')}.`;

  return (
    <figure className={`dpr-spark dpr-spark--${size}`}>
      <div className={`dpr-spark__plot dpr-spark__plot--${tone}`} role="img" aria-label={summary}>
        {bars.map((bar, index) => {
          const isCurrent = index === bars.length - 1;
          const percent = Math.max(0, Math.min(100, (bar.value / max) * 100));
          return (
            <div className="dpr-spark__column" key={index}>
              <div
                className={`dpr-spark__bar${isCurrent ? ' dpr-spark__bar--current' : ''}`}
                style={{ '--dpr-bar-h': `${percent}%` } as CSSProperties}
                /* Kept because the prototype has it and it is genuinely useful with a
                   mouse — but it is not the accessible path; the plot's own label
                   carries the whole series. */
                title={`${bar.label} · ${format(bar.value)}`}
              />
            </div>
          );
        })}
      </div>
      {captions ? (
        <figcaption className="dpr-spark__captions" aria-hidden="true">
          <span>{captions[0]}</span>
          <span>{captions[1]}</span>
        </figcaption>
      ) : null}
    </figure>
  );
}
